import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../Servicios/supabaseClient.js';
import { Button, Card, Field, Icon, StatusPill } from '../../Components/ui/ui.jsx';
import { formatWhen } from './format.js';

const STATES = {
    ok: {
        tone: 'ok', title: 'Conectado', pill: 'Activo', glyph: 'check',
        text: 'Tus flujos pueden facturar a tu nombre.',
    },
    pendiente_validacion: {
        tone: 'warn', title: 'Validando credenciales', pill: 'En curso', glyph: 'bang',
        text: 'Estamos generando tus certificados en ARCA. Suele tardar unos minutos.',
    },
    error: {
        tone: 'danger', title: 'No pudimos conectarnos', pill: 'Error', glyph: 'bang',
        text: 'ARCA rechazó la clave fiscal. Revisala abajo y volvé a conectar. Mientras tanto no se emiten facturas.',
    },
    unconfigured: {
        tone: 'neutral', title: 'Todavía no conectaste ARCA', pill: 'Sin configurar', glyph: 'bang',
        text: 'Completá tus datos para que tus flujos puedan emitir facturas.',
    },
};

const TYPES = [
    { id: 'person', label: 'Persona física', desc: 'Monotributistas y autónomos que facturan con su propio CUIT.' },
    { id: 'company', label: 'Sociedad SRL / SA', desc: 'Facturás en nombre de una empresa como representante.' },
];

const STEPS = [
    { title: 'Cargás tu CUIT y clave fiscal', text: 'Una sola vez. Si sos sociedad, también el CUIT de la empresa.' },
    { title: 'Generamos los certificados', text: 'Creamos el certificado digital y lo asociamos al servicio de facturación.' },
    { title: 'Validamos la conexión', text: 'En unos minutos queda activa y tus flujos empiezan a facturar.' },
];

export default function Arca() {
    const { user, afipStatus, setAfipStatus, notify } = useOutletContext();

    const [creds, setCreds] = useState(null);
    const [entityType, setEntityType] = useState('person');
    const [form, setForm] = useState({ cuit: '', cuit_empresa: '', password: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        supabase
            .from('afip_credentials')
            .select('status, cuit, cuit_empresa, updated_at')
            .eq('user_id', user.id)
            .maybeSingle()
            .then(({ data }) => {
                if (cancelled || !data) return;
                setCreds(data);
                setEntityType(data.cuit_empresa ? 'company' : 'person');
                setForm({ cuit: data.cuit || '', cuit_empresa: data.cuit_empresa || '', password: '' });
            });
        return () => { cancelled = true; };
    }, [user]);

    const status = STATES[afipStatus] ? afipStatus : 'unconfigured';
    const st = STATES[status];
    const isCompany = entityType === 'company';
    const configured = status !== 'unconfigured';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Guardamos CUIT y clave. Estado -> 'pendiente_validacion' para que n8n lo tome.
            const row = {
                user_id: user.id,
                cuit: form.cuit.replace(/\D/g, ''),
                cuit_empresa: isCompany ? form.cuit_empresa.replace(/\D/g, '') : null,
                afip_password: form.password,
                status: 'pendiente_validacion',
                updated_at: new Date(),
            };
            const { error } = await supabase.from('afip_credentials').upsert(row);
            if (error) throw error;

            setAfipStatus('pendiente_validacion');
            setCreds({ status: row.status, cuit: row.cuit, cuit_empresa: row.cuit_empresa, updated_at: row.updated_at.toISOString() });
            setForm((f) => ({ ...f, password: '' }));
            notify('Credenciales guardadas. Las validamos en los próximos minutos.');
        } catch (err) {
            notify(`No pudimos guardar tus credenciales: ${err.message}`, 'danger');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setEntityType(creds?.cuit_empresa ? 'company' : 'person');
        setForm({ cuit: creds?.cuit || '', cuit_empresa: creds?.cuit_empresa || '', password: '' });
    };

    return (
        <div className="page">
            <header className="pageHeaderText">
                <h1 className="pageTitle">Conexión ARCA</h1>
                <p className="pageSubtitle">Con esta conexión tus flujos emiten facturas y consultan comprobantes en ARCA (ex AFIP) a tu nombre.</p>
            </header>

            <section className={`statusHero statusHero-${st.tone}`} aria-live="polite">
                <div className="statusBadge">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
                        {st.glyph === 'check' ? <path d="M8.5 12.5l2.5 2.5 4.5-5" /> : <path d="M12 8.5v4M12 15.5v.01" />}
                    </svg>
                </div>
                <div className="statusBody">
                    <div className="statusTitleRow">
                        <span className="statusTitle">{st.title}</span>
                        <StatusPill tone={st.tone}>{st.pill}</StatusPill>
                    </div>
                    <span className="cardText" style={{ fontSize: 14, lineHeight: '20px' }}>{st.text}</span>
                </div>
                {creds && (
                    <dl className="statusFacts">
                        <div>
                            <dt>CUIT</dt>
                            <dd>{creds.cuit_empresa || creds.cuit || '-'}</dd>
                        </div>
                        <div>
                            <dt>Tipo</dt>
                            <dd>{creds.cuit_empresa ? 'Sociedad' : 'Persona física'}</dd>
                        </div>
                        <div>
                            <dt>Última actualización</dt>
                            <dd>{formatWhen(creds.updated_at)}</dd>
                        </div>
                    </dl>
                )}
            </section>

            <div className="splitWide">
                <Card featured>
                    <div className="cellStack">
                        <h2 className="qn-card-title">{configured ? 'Actualizar credenciales' : 'Conectar ARCA'}</h2>
                        <p className="cardText">Con tus datos generamos los certificados de ARCA automáticamente. No tenés que subir archivos.</p>
                    </div>

                    <div className="cellStack" style={{ gap: 8 }}>
                        <span id="arca-tipo" className="qn-field-label">¿A nombre de quién facturás?</span>
                        <div className="choiceGrid" role="group" aria-labelledby="arca-tipo">
                            {TYPES.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    className="choice"
                                    aria-pressed={entityType === t.id}
                                    onClick={() => setEntityType(t.id)}
                                >
                                    <span className="choiceRadio" aria-hidden="true" />
                                    <span className="choiceText">
                                        <span className="choiceTitle">{t.label}</span>
                                        <span className="choiceDesc">{t.desc}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <form className="form" onSubmit={handleSubmit}>
                        {isCompany && (
                            <Field
                                id="arca-cuit-empresa"
                                label="CUIT de la empresa"
                                inputMode="numeric"
                                placeholder="30123456789"
                                hint="El CUIT de la sociedad que vas a facturar."
                                required
                                value={form.cuit_empresa}
                                onChange={(e) => setForm({ ...form, cuit_empresa: e.target.value })}
                            />
                        )}
                        <div className="grid2">
                            <Field
                                id="arca-cuit"
                                label={isCompany ? 'CUIT del representante' : 'CUIT'}
                                inputMode="numeric"
                                placeholder="20123456789"
                                required
                                value={form.cuit}
                                onChange={(e) => setForm({ ...form, cuit: e.target.value })}
                            />
                            <Field
                                id="arca-clave"
                                label="Clave fiscal"
                                type="password"
                                autoComplete="off"
                                placeholder="••••••••"
                                required
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                            />
                        </div>
                        <div className="formActions">
                            {configured && <Button onClick={resetForm} disabled={saving}>Cancelar</Button>}
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? 'Guardando…' : configured ? 'Guardar y validar' : 'Conectar'}
                            </Button>
                        </div>
                    </form>
                </Card>

                <Card>
                    <h2 className="h3">Cómo funciona</h2>
                    <ol className="steps">
                        {STEPS.map((s, i) => (
                            <li key={s.title} className="step">
                                <span className="stepNum" aria-hidden="true">{i + 1}</span>
                                <div className="stepText">
                                    <span className="stepTitle">{s.title}</span>
                                    <span className="cardText">{s.text}</span>
                                </div>
                            </li>
                        ))}
                    </ol>
                    <div className="noteBox">
                        <Icon name="lock" size={18} style={{ color: 'var(--primary)' }} />
                        Tu clave fiscal solo la usan tus flujos para hablar con ARCA. No se muestra en ninguna pantalla.
                    </div>
                </Card>
            </div>
        </div>
    );
}
